/** PG / internal-user dashboard + home section copy keyed by variant. */

import {
  DUPLICATE_ACCOUNTS_MERGE_LOCKED_USER_MESSAGE,
} from "../../globals/utils/duplicateAccountsMergeFeature";
import {
  dashboardLink,
  dashboardLinks,
  dashboardLinksWithFeature,
} from "./dashboardLinkTemplates";

export const PG_HOME_VARIANT = Object.freeze({
  guardianUser: "guardianUser",
  contact: "contact",
});

export const IU_HOME_VARIANT = Object.freeze({
  internalUser: "internalUser",
  admin: "admin",
});

const SHARED_COPY = {
  dashboardTitle: "Professional Guardian Dashboard",
  sections: {
    activity: true,
    indemnity: true,
    drafts: true,
  },
  activity: {
    title: "Recent Activity",
    subtitle: "Your latest interactions and updates",
    emptyActivity: "No recent activity.",
    seeMore: "See more",
    loadMoreLabel: "See more",
    viewBond: "View Bond",
    goBack: "Go back",
  },
  indemnity: {
    title: "Pending Signatures",
    subtitle: "Bonds with pending signed indemnity agreements",
    emptyList: "No bonds with Pending Signatures.",
    view: "View",
    markCompleted: "Mark as completed",
  },
  drafts: {
    title: "Draft Bond Requests",
    subtitle: "Continue working on your saved drafts.",
    emptyDrafts: "You have no saved bond requests.",
    resume: "Resume",
    delete: "Delete",
  },
};

const IU_SHARED_COPY = {
  dashboardTitle: "Vermost Internal User Dashboard",
  sections: {
    activity: true,
    indemnity: true,
    drafts: true,
    assignmentAlerts: true,
  },
  activity: {
    title: "Recent Activity",
    subtitle: "Your latest interactions and updates",
    emptyActivity: "No recent active bonds.",
    seeMore: "See more",
    loadMoreLabel: "See more",
    viewBond: "View Bond",
    goBack: "Go back",
  },
  indemnity: SHARED_COPY.indemnity,
  drafts: SHARED_COPY.drafts,
  assignment: {
    title: "Assignment Alerts",
    subtitle: "Bonds missing attorney assignment",
    emptyList: "No bonds with missing assignments.",
    view: "View",
  },
};

const VARIANT_META = {
  [PG_HOME_VARIANT.guardianUser]: {
    pgIdSource: "api",
    ...SHARED_COPY,
  },
  [PG_HOME_VARIANT.contact]: {
    pgIdSource: "auth",
    ...SHARED_COPY,
  },
  [IU_HOME_VARIANT.internalUser]: {
    ...IU_SHARED_COPY,
  },
  [IU_HOME_VARIANT.admin]: {
    ...IU_SHARED_COPY,
    dashboardTitle: "Vermost System Admin Dashboard",
  },
};

/**
 * @param {'guardianUser'|'contact'|'internalUser'|'admin'} variant
 */
export function getPgHomeVariantConfig(variant) {
  return VARIANT_META[variant] || VARIANT_META[PG_HOME_VARIANT.guardianUser];
}

function filterByFeature(options, userFeatures) {
  return options.filter((opt) => userFeatures.includes(opt.feature));
}

const IU_DATA_MGMT_OPTIONS = dashboardLinksWithFeature([
  "bondArchives",
  "analytics",
  "createHistoricBond",
]);

const IU_ACCOUNT_MGMT_OPTIONS = dashboardLinksWithFeature([
  "internalUsers",
  "lawFirms",
  "professionalGuardians",
  "legalServiceParalegals",
  "lawFirmUsers",
]);

const IU_SYSTEM_MGMT_OPTIONS = dashboardLinksWithFeature([
  "approveBonds",
  "managePremiums",
  "bondTypes",
  "bondNumbers",
  "carriers",
  "states",
  "allEnhancementRequestsIu",
  "supportRequests",
  "announcements",
]);

/**
 * System-admin dashboard link groups (not feature-filtered).
 * @param {boolean} duplicateAccountsMergeEnabled
 */
export function buildAdminDashboardLinkGroups(duplicateAccountsMergeEnabled = true) {
  return [
    {
      action_value: "Bond Operations",
      action_description:
        "Covers end-to-end surety bond workflows such as creating, reviewing submissions.",
      values: [
        dashboardLink("requestBond"),
        dashboardLink("approveBonds"),
        dashboardLink("uploadIndemnityAdmin"),
      ],
    },
    {
      action_value: "Management",
      action_description: "Manage accounts, data, and system configuration in one place.",
      values: [
        {
          name: "Account Management",
          path: "/admin_account_management",
          iconKey: "adminPanelSettings",
          options: [
            ...dashboardLinks([
              "internalUsers",
              "lawFirms",
              "professionalGuardians",
              "legalServiceParalegals",
              "lawFirmUsers",
            ]),
            {
              name: "Duplicate Accounts",
              path: "/vermost_duplicate_accounts",
              iconKey: "deleteOutline",
              disabled: !duplicateAccountsMergeEnabled,
              disabledHint: DUPLICATE_ACCOUNTS_MERGE_LOCKED_USER_MESSAGE,
            },
          ],
        },
        {
          name: "Data Management",
          path: "/admin_data_management",
          iconKey: "storage",
          options: dashboardLinks(["bondArchives", "analytics", "createHistoricBond"]),
        },
        {
          name: "System Management",
          path: "/admin_system_management",
          iconKey: "settings",
          options: [
            ...dashboardLinks([
              "approveBonds",
              "managePremiums",
              "bondTypes",
              "bondNumbers",
              "carriers",
              "states",
              "obligees",
              "lawSchools",
              "allEnhancementRequestsAdmin",
              "supportRequests",
              "announcements",
            ]),
          ],
        },
      ],
    },
    {
      action_value: "Tools",
      action_description: "Utilities and calculators to support your workflows.",
      values: [dashboardLink("bondCalculator")],
    },
  ];
}

/**
 * Internal-user dashboard link groups (`iconKey`: map in consumer to `@mui/icons-material`).
 * @param {string[]} userFeatures
 */
export function buildInternalUserDashboardLinkGroups(userFeatures = []) {
  const accountManagementOptions = filterByFeature(IU_ACCOUNT_MGMT_OPTIONS, userFeatures);
  const dataManagementOptions = filterByFeature(IU_DATA_MGMT_OPTIONS, userFeatures);
  const systemManagementOptions = filterByFeature(IU_SYSTEM_MGMT_OPTIONS, userFeatures);

  return [
    {
      action_value: "Bond Operations",
      action_description:
        "Covers end-to-end surety bond workflows such as creating and reviewing submissions.",
      values: [dashboardLink("requestBond")],
    },
    {
      action_value: "Management",
      action_description: "Manage accounts, data, and system configuration in one place.",
      values: [
        { name: "Account Management", iconKey: "supervisorAccount", options: accountManagementOptions },
        { name: "Data Management", iconKey: "storage", options: dataManagementOptions },
        { name: "System Management", iconKey: "settings", options: systemManagementOptions },
      ],
    },
    {
      action_value: "Tools",
      action_description: "Utilities and calculators to support your workflows.",
      values: [dashboardLink("bondCalculator")],
    },
  ];
}

/**
 * Dashboard link groups factory for PG / IU / admin home shells.
 */
export function buildDashboardLinkGroups({
  variant,
  pg,
  userFeatures = [],
  duplicateAccountsMergeEnabled = true,
}) {
  if (variant === IU_HOME_VARIANT.admin) {
    return buildAdminDashboardLinkGroups(duplicateAccountsMergeEnabled);
  }
  if (variant === IU_HOME_VARIANT.internalUser) {
    return buildInternalUserDashboardLinkGroups(userFeatures);
  }
  return buildPgDashboardLinkGroups(pg);
}

/**
 * Link metadata for PGDashboard (`iconKey`: map in consumer to `@mui/icons-material`).
 * @param {string|null|undefined} pg
 */
export function buildPgDashboardLinkGroups(pg) {
  return [
    {
      action_value: "Bond Management",
      action_description: "Manage your bond requests and view existing bonds",
      values: [
        dashboardLink("requestBond"),
        {
          name: "View Bonds",
          path: `/professional_guardian_view_bonds/${pg ?? ""}`,
          iconKey: "thumbUp",
        },
        dashboardLink("uploadIndemnityPg"),
      ],
    },
    {
      action_value: "Tools & Requests",
      action_description: "Useful tools and enhancement requests",
      values: [dashboardLink("premiumCalculator"), dashboardLink("enhancementRequests")],
    },
  ];
}
